import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Link,
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Divider,
  useTheme,
  Tooltip,
} from '@mui/material';
import { useMediaQuery } from '@material-ui/core';
import HotelSharpIcon from '@mui/icons-material/HotelSharp';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import StarRateSharpIcon from '@mui/icons-material/StarRateSharp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import {
  POINTER_GREEN,
  COLOR_PRIMARY,
  COLOR_RED,
  COLOR_YELLOW,
  COLOR_GREEN,
  INDIVIDUAL_LISTING_TOOLTIP_BUTTON_COLOR,
  INDIVIDUAL_LISTING_TOOLTIP_BUTTON_HOVER_COLOR,
  exchangeRates,
} from 'src/utils/const';

import {
  formatCurrency,
  convertCurrency,
  abbreviateNumber,
  checkListingRecentlyAdded,
  checkListingInactive,
  checkListingUnlisted,
  checkAdrAnomaly,
} from 'src/utils/functions';
import { PointerTooltipBodyPropsType } from 'src/interfaces/iproptypes';
import { Listing } from 'src/interfaces/idashboard';
import airbnb from 'src/App/assets/airbnbMini.svg';
import bookingCom from 'src/App/assets/bookingComMini.jpeg';
import vrbo from 'src/App/assets/vrboMini.png';

import { COLOR_PRIMARY_1, COLOR_TEXT_4 } from 'src/utils/colors';
import {
  RecentlyAddedChip,
  RecentlyAddedClockIcon,
} from 'src/App/Dashboards/Market/Components/Chips/RecentlyAddedChip';
import ListingInactivityChip from 'src/App/Dashboards/Market/Components/Chips/ListingInactivityChip';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import { logAmplitudeEvent } from 'src/utils/analytics';
import UnlistedChip from 'src/App/Dashboards/Market/Components/Chips/UnlistedChip';
import UnavailableIcon from 'src/App/Dashboards/Market/Components/Icons/UnavailableIcon';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';

const styles: any = {
  contentIconStyle: {
    fontSize: 23,
    color: POINTER_GREEN,
    padding: 3,
  },
  confidenceScoreIconOuter: {
    width: 50,
    height: 50,
    borderRadius: 50,
    background: `linear-gradient(90deg, ${COLOR_RED} 0%, ${COLOR_YELLOW} 35%, ${COLOR_GREEN} 100%)`,
    position: 'absolute',
    top: 7,
    left: 7,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  confidenceScoreIconInner: {
    width: 44,
    height: 44,
    borderRadius: 44,
    background: '#123',
    cursor: 'pointer',
  },
  cardStyle: {
    position: 'relative',
    minWidth: '300px',
    maxWidth: '350px',
    overflow: 'visible',
  },
  cardBackground: {
    display: 'flex',
    minWidth: '300px',
    maxWidth: '350px',
    position: 'relative',
    overflow: 'visible',
    background: '#fff',
    borderRadius: 4,
  },
  blurText: {
    fontSize: 24,
    fontWeight: 900,
    color: POINTER_GREEN,
    filter: 'blur(5px)',
    display: 'inline-block',
  },
  mobileViewImage: {
    cursor: 'pointer',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    border: `4px solid #eee`,
    position: 'absolute',
    top: -40,
    left: 10,
    boxShadow: '0 -4px 10px #aaa',
    background: '#fff',
  },
};

const MetricsBox = ({
  metric,
  title,
  isDisabled,
  isSmall,
  addDivider,
  demoMetrics,
  openListingModal,
  showLock,
}: {
  metric: string | JSX.Element;
  title: string;
  isDisabled: boolean | undefined;
  isSmall?: boolean;
  addDivider?: boolean;
  demoMetrics?: string;
  openListingModal: any;
  showLock?: boolean;
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const showLockState = showLock || isDisabled;

  return (
    <Box
      sx={{
        width: '100%',
        ...(addDivider ? { borderLeft: '1px solid #ccc', borderRight: '1px solid #ccc' } : {}),
      }}
    >
      <Grid item xs={12} justifyContent={'center'} alignItems={'center'}>
        {!showLockState ? (
          <Typography
            variant={matches ? 'h6' : 'h5'}
            sx={{ fontSize: isSmall ? '16px' : '18px', fontWeight: 'bold', textAlign: 'center' }}
          >
            {typeof metric == 'string' && metric.includes('NaN') ? 'N/A' : metric}
          </Typography>
        ) : (
          <Box
            onClick={openListingModal}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: COLOR_PRIMARY_1,
              fontSize: isSmall ? '14px' : '16px',
              fontWeight: 600,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant={matches ? 'h6' : 'h5'} sx={{ fontSize: isSmall ? '14px' : '16px' }}>
              Unlock to view
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} justifyContent={'center'} alignItems={'center'}>
        <Typography
          variant={matches ? 'h6' : 'h5'}
          sx={{ fontSize: '11px', fontWeight: 'normal', textAlign: 'center' }}
        >
          {title}
        </Typography>
      </Grid>
    </Box>
  );
};

const SimplifiedAirbnbPointerBody = ({
  data,
  currency,
  canDelete,
  isDisabled,
  deleteListing,
  setOpenFullView,
  estimatorSampleListingData,
  isHidden,
  isEstimator,
  setTopPerformingListingOnFocus,
  setTopPerformingListingModalOpen,
  handleOnSampleClick,
  hiddenListingIds,
  addHiddenListingId,
  removeHiddenListingId,
  isEstimatorLocked,
  showHighResListingImage,
  disabledViewMoreButtonAction,
  isCompsSelectedFromCalculatorCompsSelection,
  handleCompsAdditionCalculatorCompsSection,
  showDesktopUi,
  referenceADR,
  isBookingComListing,
  ota,
}: PointerTooltipBodyPropsType) => {
  const isMobileView = useMediaQuery('(max-width:550px)');
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));

  const [confidanceScoreOpen, setConfidanceScoreOpen] = useState<boolean>(false);

  const isListingRecentlyAdded = checkListingRecentlyAdded(data as Listing, isBookingComListing);
  const isListingInactive = checkListingInactive(data as Listing, isBookingComListing);
  const isListingUnlisted = checkListingUnlisted(data as Listing, isBookingComListing);
  const isAdrAnomaly = checkAdrAnomaly((data as Listing).adrLTM, referenceADR || null);

  const currencySymbol = exchangeRates[currency].symbol;
  //Revenue Potential
  const revenuePotential = estimatorSampleListingData
    ? estimatorSampleListingData.revenuePotential
    : data?.['revenuePotential'];
  const formattedRevenuePotential = `${currencySymbol}${abbreviateNumber(
    convertCurrency(currency, revenuePotential)
  )}`;

  const listingUrl = estimatorSampleListingData
    ? estimatorSampleListingData.listing_url
    : `airbnb.com/rooms/${data?.['listingID']}`;

  //ADR
  let adr: number = -1;
  let formattedADR: string = '-';

  if (!estimatorSampleListingData) {
    adr = data?.['adrLTM'] ? data?.['adrLTM'] : -1;
    formattedADR =
      adr !== -1
        ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, adr)))}`
        : '-';
  }

  const activeDaysCount =
    data['activeDaysCount'] && !isNaN(data['activeDaysCount'])
      ? Math.min(365, data['activeDaysCount'])
      : data['activeDaysCount'];

  let dataToUse = data;

  // Hard-coded mock signature: revenue and revenue potential both 1234 (actual case ~0.0001%)
  // Also respect isMockListingData from report comps (convertData) where currency conversion can change 1234
  const isMockListingData =
    data?.['isMockListingData'] === true ||
    (data?.['revenuePotential'] === 1234 && data?.['annualRevenueLTM'] === 1234);

  const getTextColor = (color: string) => {
    return isHidden ? '#ccc' : color;
  };

  const getThumbnailImageUrl = (url: string, isBookingComListing: boolean = false) => {
    if (!url) return '';

    if (isBookingComListing) {
      return url;
    }

    if (!showHighResListingImage) return url;

    try {
      return url.replace('?aki_policy=small', '');
    } catch (err) {
      return url;
    }
  };

  const HandleHideListingClick = () => {
    if (hiddenListingIds && !isEstimatorLocked) {
      if (hiddenListingIds.includes(data['listingID'])) {
        removeHiddenListingId(data['listingID']);
      } else {
        addHiddenListingId(data['listingID']);
      }
    }
  };

  const openListingModal = isDisabled => {
    setOpenFullView(true);
    setTopPerformingListingModalOpen && setTopPerformingListingModalOpen(data);
  };

  const openListingPage = (isBookingComListing: boolean) => {
    if (isBookingComListing) {
      window.open(dataToUse?.['listingURL'], '_blank');
    } else {
      window.open(`https://www.airbnb.com/rooms/${dataToUse?.['listingID']}`, '_blank');
    }
  };

  const CompsAddButton =
    isCompsSelectedFromCalculatorCompsSelection !== undefined ? (
      <Button
        fullWidth
        sx={{
          marginTop: '10px',
          background: COLOR_PRIMARY + 'dd',
          color: COLOR_PRIMARY,
          borderRadius: '4px',
          '&:hover': {
            color: '#fff',
            background: COLOR_PRIMARY + 'aa',
          },
        }}
        onClick={() =>
          handleCompsAdditionCalculatorCompsSection && handleCompsAdditionCalculatorCompsSection()
        }
      >
        {isCompsSelectedFromCalculatorCompsSelection ? (
          <DoneRoundedIcon sx={{ color: 'white' }} />
        ) : (
          <AddRoundedIcon sx={{ color: 'white' }} />
        )}
      </Button>
    ) : (
      <></>
    );

  const trackListingPageViewed = (source: string) => {
    logAmplitudeEvent('listing_modal_opened', { visited_from: source });
  };

  const OverviewDesktopPointer = () => (
    <Card
      // onMouseEnter={() => {
      //   setTopPerformingListingOnFocus && setTopPerformingListingOnFocus(_ => data?.['listingID']);
      // }}
      // onMouseLeave={() =>
      //   setTopPerformingListingOnFocus && setTopPerformingListingOnFocus(_ => null)
      // }
      sx={{
        ...styles.cardStyle,
        minWidth: '100%',
        width: '100%',
        transformOrigin: 'top left',
        transform: 'scale(1)',
        padding: '0 !important',
      }}
    >
      <CardMedia
        component="img"
        sx={{
          width: '100%',
          height: '100px',
          objectFit: 'cover',
          borderRadius: 2,
          cursor: 'pointer',
          filter: isHidden ? 'saturate(0)' : 'saturate(1)',
          opacity: isHidden ? 0.6 : 1,
        }}
        alt={dataToUse?.['name']}
        image={getThumbnailImageUrl(dataToUse?.['thumbnail_url'], isBookingComListing)}
        title={dataToUse?.['name']}
        onClick={() =>
          isBookingComListing ? openListingPage(isBookingComListing) : openListingModal(!isHidden)
        }
      />

      {/* primary ota */}
      <Tooltip title={`View on ${isBookingComListing ? 'Booking.com' : 'Airbnb'}`}>
        <Box
          sx={{
            position: 'absolute',
            top: '5px',
            left: '5px',
            background: '#fff',
            padding: '4px',
            borderRadius: 200,
            width: '30px',
            height: '30px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
          onClick={() => openListingPage(isBookingComListing || false)}
        >
          <img
            src={isBookingComListing ? bookingCom : airbnb}
            style={{
              width: '25px',
              height: '25px',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Tooltip>

      {dataToUse?.['bookingComURL'] && (
        <Tooltip title="View on Booking.com">
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              left: '50px',
              background: '#fff',
              padding: '4px',
              borderRadius: 200,
              width: '30px',
              height: '30px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
            }}
            onClick={() => {
              window.open(
                `https://www.booking.com/hotel/${dataToUse?.['bookingComURL']}`,
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            <img
              src={bookingCom}
              style={{
                width: '40px',
                height: '40px',
              }}
            />
          </Box>
        </Tooltip>
      )}

      {dataToUse?.['vrboURL'] && (
        <Tooltip title="View on VRBO">
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              left: dataToUse?.['bookingComURL'] ? '95px' : '50px',
              background: '#fff',
              padding: '4px',
              borderRadius: 200,
              width: '30px',
              height: '30px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              window.open(`https://www.vrbo.com/${dataToUse?.['vrboURL']}`, '_blank');
            }}
          >
            <img
              src={vrbo}
              style={{
                width: '25px',
                height: '25px',
              }}
            />
          </Box>
        </Tooltip>
      )}

      {isListingRecentlyAdded && (
        <Box
          sx={{
            position: 'absolute',
            top: '5px',
            right: '5px',
          }}
        >
          <RecentlyAddedChip />
        </Box>
      )}
      {isListingInactive && (
        <Box
          sx={{
            position: 'absolute',
            top: '5px',
            right: '5px',
          }}
        >
          <ListingInactivityChip activeDaysCount={activeDaysCount} />
        </Box>
      )}
      {isListingUnlisted && (
        <Box
          sx={{
            position: 'absolute',
            top: '5px',
            right: '5px',
          }}
        >
          <UnlistedChip />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <CardContent sx={{ flex: '1 0 auto', paddingTop: '15px', paddingBottom: 0 }}>
          <Grid container justifyContent={'space-between'} alignItems={'center'}>
            <Grid item xs={8}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 'bold', textTransform: 'capitalize', fontSize: '15px' }}
              >
                {/* Cannot read properties of undefined (reading 'split') */}
                {dataToUse?.['room_type']?.split('_').join(' ')} & {dataToUse?.['bedrooms']}{' '}
                {dataToUse?.['bedrooms'] == 'Studio' ? '' : 'Bed'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="body1"
                sx={{ fontSize: '13px', fontWeight: 'bold', float: 'right' }}
              >
                <pre>
                  ★{dataToUse?.['review_scores_rating']} (
                  {abbreviateNumber(dataToUse?.['visible_review_count'])})
                </pre>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: '10px' }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: '14px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  overflow: 'hidden',
                  color: COLOR_TEXT_4,
                }}
              >
                {dataToUse?.['name']}
              </Typography>
            </Grid>
            <Grid
              container
              item
              xs={12}
              spacing={2}
              justifyContent={'center'}
              alignItems={'center'}
              sx={{ marginTop: '10px' }}
            >
              <Grid item xs={6}>
                <MetricsBox
                  title={'Revenue Potential'}
                  metric={
                    isListingRecentlyAdded ? (
                      <RecentlyAddedClockIcon />
                    ) : isAdrAnomaly ? (
                      <UnavailableIcon />
                    ) : (
                      `${formattedRevenuePotential}`
                    )
                  }
                  isDisabled={isDisabled || isHidden}
                  demoMetrics={`${exchangeRates[currency].symbol}109`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                  showLock={isMockListingData}
                />
              </Grid>
              <Grid item xs={6}>
                <MetricsBox
                  title={'Days Available'}
                  metric={
                    isListingRecentlyAdded ? <RecentlyAddedClockIcon /> : activeDaysCount + ''
                  }
                  isDisabled={isDisabled || isHidden}
                  demoMetrics={`300`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                />
              </Grid>
            </Grid>

            <Divider component="div" sx={{ width: '100%' }} />

            <Grid
              container
              item
              xs={12}
              spacing={1}
              justifyContent={'center'}
              alignItems={'center'}
              sx={{ marginTop: '5px' }}
            >
              <Grid item xs={4}>
                <MetricsBox
                  title={'Revenue'}
                  metric={
                    isListingRecentlyAdded ? (
                      <RecentlyAddedClockIcon />
                    ) : isAdrAnomaly ? (
                      <UnavailableIcon />
                    ) : (
                      `${currencySymbol}${abbreviateNumber(
                        convertCurrency(currency, data?.['annualRevenueLTM'])
                      )}`
                    )
                  }
                  isDisabled={isDisabled || isHidden}
                  isSmall={true}
                  demoMetrics={`${exchangeRates[currency].symbol}75,350`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                  showLock={isMockListingData}
                />
              </Grid>
              <Grid item xs={4}>
                <MetricsBox
                  title={'Occupancy Rate'}
                  metric={
                    isListingRecentlyAdded ? (
                      <RecentlyAddedClockIcon />
                    ) : (
                      data?.['occupancyRateLTM'] + '%'
                    )
                  }
                  isDisabled={isDisabled || isHidden}
                  isSmall={true}
                  addDivider={true}
                  demoMetrics={`70%`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                  showLock={isMockListingData}
                />
              </Grid>
              <Grid item xs={4}>
                <MetricsBox
                  title={'Daily Rate'}
                  metric={
                    isListingRecentlyAdded && isBookingComListing === false ? (
                      <RecentlyAddedClockIcon />
                    ) : isAdrAnomaly && isBookingComListing === false ? (
                      <UnavailableIcon />
                    ) : (
                      `${currencySymbol}${abbreviateNumber(
                        convertCurrency(currency, data?.['adrLTM'])
                      )}`
                    )
                  }
                  isDisabled={false}
                  isSmall={true}
                  demoMetrics={`${exchangeRates[currency].symbol}96`}
                  openListingModal={() => openListingModal(isDisabled || isHidden)}
                  showLock={isMockListingData}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              {isCompsSelectedFromCalculatorCompsSelection !== undefined ? (
                CompsAddButton
              ) : (
                <Button
                  fullWidth
                  onClick={() => {
                    isBookingComListing
                      ? openListingPage(isBookingComListing)
                      : openListingModal(!isHidden);
                    trackListingPageViewed('overview');
                  }}
                  sx={{
                    marginTop: '10px',
                    background: COLOR_PRIMARY + '22',
                    color: COLOR_PRIMARY,
                    borderRadius: '4px',
                    '&:hover': {
                      color: '#fff',
                      background: COLOR_PRIMARY,
                    },
                  }}
                >
                  View Details
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Box>
    </Card>
  );

  const OverviewMobilePointer = () => (
    <Card
      // onMouseEnter={() => {
      //   setTopPerformingListingOnFocus && setTopPerformingListingOnFocus(_ => data?.['listingID']);
      // }}
      // onMouseLeave={() =>
      //   setTopPerformingListingOnFocus && setTopPerformingListingOnFocus(_ => null)
      // }
      sx={{ ...styles.cardStyle, maxWidth: 500, padding: '0 !important' }}
    >
      <Box sx={{ ...styles.cardBackground, maxWidth: 500 }}>
        <CardMedia
          component="img"
          sx={{
            width: '30%',
            borderRadius: 2,
            cursor: 'pointer',
          }}
          alt={dataToUse?.['name']}
          image={getThumbnailImageUrl(dataToUse?.['thumbnail_url'], isBookingComListing)}
          title={dataToUse?.['name']}
          onClick={() =>
            isBookingComListing ? openListingPage(isBookingComListing) : openListingModal(!isHidden)
          }
        />
        <Tooltip title={`View on ${isBookingComListing ? 'Booking.com' : 'Airbnb'}`}>
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              left: '5px',
              background: '#fff',
              padding: '4px',
              borderRadius: 200,
              width: '30px',
              height: '30px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
            }}
            onClick={() => openListingPage(isBookingComListing || false)}
          >
            <img
              src={isBookingComListing ? bookingCom : airbnb}
              style={{
                width: '25px',
                height: '25px',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Tooltip>

        {dataToUse?.['bookingComURL'] && (
          <Tooltip title="View on Booking.com">
            <Box
              sx={{
                position: 'absolute',
                top: '5px',
                left: '50px',
                background: '#fff',
                padding: '4px',
                borderRadius: 200,
                width: '30px',
                height: '30px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                overflow: 'hidden',
              }}
              onClick={() => {
                window.open(
                  `https://www.booking.com/hotel/${dataToUse?.['bookingComURL']}`,
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <img
                src={bookingCom}
                style={{
                  width: '40px',
                  height: '40px',
                }}
              />
            </Box>
          </Tooltip>
        )}

        {dataToUse?.['vrboURL'] && (
          <Tooltip title="View on VRBO">
            <Box
              sx={{
                position: 'absolute',
                top: dataToUse?.['bookingComURL'] ? '50px' : '5px',
                left: dataToUse?.['bookingComURL'] ? '5px' : '50px',
                background: '#fff',
                padding: '4px',
                borderRadius: 200,
                width: '30px',
                height: '30px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => {
                window.open(`https://www.vrbo.com/${dataToUse?.['vrboURL']}`, '_blank');
              }}
            >
              <img
                src={vrbo}
                style={{
                  width: '25px',
                  height: '25px',
                }}
              />
            </Box>
          </Tooltip>
        )}

        {isListingRecentlyAdded && (
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
            }}
          >
            <RecentlyAddedChip />
          </Box>
        )}
        {isListingInactive && (
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
            }}
          >
            <ListingInactivityChip activeDaysCount={activeDaysCount} />
          </Box>
        )}
        {isListingUnlisted && (
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
            }}
          >
            <UnlistedChip />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '70% !important' }}>
          <CardContent
            sx={{ flex: '1 0 auto', paddingTop: '15px', paddingBottom: '15px !important' }}
          >
            <Grid container justifyContent={'space-between'} alignItems={'center'}>
              <Grid item xs={8}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 'bold', textTransform: 'capitalize', fontSize: '15px' }}
                >
                  {dataToUse?.['room_type']?.split('_').join(' ')} & {dataToUse?.['bedrooms']}{' '}
                  {dataToUse?.['bedrooms'] == 'Studio' ? '' : 'Bed'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="body1"
                  sx={{ fontSize: '13px', fontWeight: 'bold', float: 'right' }}
                >
                  <pre>
                    ★{dataToUse?.['review_scores_rating']} (
                    {abbreviateNumber(dataToUse?.['visible_review_count'])})
                  </pre>
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '10px' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '14px',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    overflow: 'hidden',
                    color: COLOR_TEXT_4,
                  }}
                >
                  {dataToUse?.['name']}
                </Typography>
              </Grid>
              <Grid
                container
                item
                xs={12}
                spacing={2}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{ marginTop: '10px' }}
              >
                <Grid item xs={6}>
                  <MetricsBox
                    title={'Revenue Potential'}
                    metric={
                      isListingRecentlyAdded ? (
                        <RecentlyAddedClockIcon />
                      ) : isAdrAnomaly ? (
                        <UnavailableIcon />
                      ) : (
                        `${formattedRevenuePotential}`
                      )
                    }
                    isDisabled={isDisabled || isHidden}
                    demoMetrics={`${exchangeRates[currency].symbol}109`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                    showLock={isMockListingData}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MetricsBox
                    title={'Days Available'}
                    metric={
                      isListingRecentlyAdded ? <RecentlyAddedClockIcon /> : activeDaysCount + ''
                    }
                    isDisabled={isDisabled || isHidden}
                    demoMetrics={`300`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                  />
                </Grid>
              </Grid>

              <Divider component="div" sx={{ width: '100%' }} />

              <Grid
                container
                item
                xs={12}
                spacing={1}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{ marginTop: '5px' }}
              >
                <Grid item xs={4}>
                  <MetricsBox
                    title={'Revenue'}
                    metric={
                      isListingRecentlyAdded ? (
                        <RecentlyAddedClockIcon />
                      ) : isAdrAnomaly ? (
                        <UnavailableIcon />
                      ) : (
                        `${currencySymbol}${abbreviateNumber(
                          convertCurrency(currency, data?.['annualRevenueLTM'])
                        )}`
                      )
                    }
                    isDisabled={isDisabled || isHidden}
                    isSmall={true}
                    demoMetrics={`${exchangeRates[currency].symbol}75,350`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                    showLock={isMockListingData}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MetricsBox
                    title={'Occupancy Rate'}
                    metric={
                      isListingRecentlyAdded ? (
                        <RecentlyAddedClockIcon />
                      ) : (
                        data?.['occupancyRateLTM'] + '%'
                      )
                    }
                    isDisabled={isDisabled || isHidden}
                    isSmall={true}
                    addDivider={true}
                    demoMetrics={`70%`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                    showLock={isMockListingData}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MetricsBox
                    title={'Daily Rate'}
                    metric={
                      isListingRecentlyAdded && isBookingComListing === false ? (
                        <RecentlyAddedClockIcon />
                      ) : isAdrAnomaly && isBookingComListing === false ? (
                        <UnavailableIcon />
                      ) : (
                        `${currencySymbol}${abbreviateNumber(
                          convertCurrency(currency, data?.['adrLTM'])
                        )}`
                      )
                    }
                    isDisabled={false}
                    isSmall={true}
                    demoMetrics={`${exchangeRates[currency].symbol}96`}
                    openListingModal={() => openListingModal(isDisabled || isHidden)}
                    showLock={isMockListingData}
                  />
                </Grid>
              </Grid>

              <Button
                fullWidth
                onClick={() => {
                  isBookingComListing
                    ? openListingPage(isBookingComListing)
                    : openListingModal(!isHidden);
                  trackListingPageViewed('overview');
                }}
                sx={{
                  marginTop: '10px',
                  background: COLOR_PRIMARY + '22',
                  color: COLOR_PRIMARY,
                  borderRadius: '4px',
                  '&:hover': {
                    color: '#fff',
                    background: COLOR_PRIMARY,
                  },
                }}
              >
                View Details
              </Button>
              {CompsAddButton}
            </Grid>
          </CardContent>
        </Box>
      </Box>
    </Card>
  );

  const EstimatorPointer = ({ isBookingComListing = false }: { isBookingComListing: boolean }) => (
    <Card sx={{ ...styles.cardStyle, maxWidth: 500 }}>
      <Box sx={{ ...styles.cardBackground, maxWidth: 500 }}>
        {isMobileView && (
          <img
            style={styles.mobileViewImage}
            alt={dataToUse?.['name']}
            src={getThumbnailImageUrl(dataToUse?.['thumbnail_url'], isBookingComListing)}
            onClick={() =>
              isBookingComListing
                ? openListingPage(isBookingComListing)
                : !isHidden && setOpenFullView(true)
            }
          />
        )}

        {!isMobileView && (
          <CardMedia
            component="img"
            sx={{
              width: '30%',
              borderRadius: 2,
              cursor: 'pointer',
              filter: isHidden ? 'saturate(0)' : 'saturate(1)',
              opacity: isHidden ? 0.6 : 1,
            }}
            alt={dataToUse?.['name']}
            image={getThumbnailImageUrl(dataToUse?.['thumbnail_url'], isBookingComListing)}
            title={dataToUse?.['name']}
            onClick={() =>
              isBookingComListing
                ? openListingPage(isBookingComListing)
                : !isHidden && setOpenFullView(true)
            }
          />
        )}

        {isListingRecentlyAdded && (
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
            }}
          >
            <RecentlyAddedChip />
          </Box>
        )}
        {isListingInactive && (
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
            }}
          >
            <ListingInactivityChip activeDaysCount={activeDaysCount} />
          </Box>
        )}
        {isListingUnlisted && (
          <Box
            sx={{
              position: 'absolute',
              top: '5px',
              right: '5px',
            }}
          >
            <UnlistedChip />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '500px !important' }}>
          <CardContent sx={{ flex: '1 0 auto', paddingTop: '25px' }}>
            <Typography
              component="div"
              variant="h5"
              sx={{
                fontSize: isMobileView ? '16px' : '18px',
                fontWeight: 'bold',
                pb: 1,
                color: getTextColor('#000'),
              }}
            >
              <Link
                target="_blank"
                rel="nofollow"
                color="inherit"
                underline="none"
                onClick={() => {
                  setOpenFullView(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                {dataToUse?.['name']}
              </Link>
              {dataToUse?.['host_name'] && (
                <>
                  <span style={{ color: getTextColor(POINTER_GREEN) }}> by </span>
                  <Link
                    href={'https://www.airbnb.com/users/show/' + dataToUse?.['hostId']}
                    target="_blank"
                    rel="nofollow"
                    color={getTextColor(POINTER_GREEN)}
                  >
                    {dataToUse?.['host_name']}
                  </Link>
                </>
              )}
            </Typography>
            <Typography
              component={'span'}
              color={getTextColor('textSecondary')}
              sx={{ fontSize: isMobileView ? '12px' : '14px' }}
            >
              <Grid container>
                <Grid item xs={2}>
                  <HotelSharpIcon
                    style={{ ...styles.contentIconStyle, color: getTextColor(POINTER_GREEN) }}
                  />{' '}
                  {dataToUse?.['bedrooms']}
                </Grid>

                <Grid item xs={10}>
                  {dataToUse?.['review_scores_rating'] > 0 ? (
                    <StarRateSharpIcon
                      style={{ ...styles.contentIconStyle, color: getTextColor(POINTER_GREEN) }}
                    />
                  ) : (
                    ''
                  )}
                  {dataToUse?.['review_scores_rating'] > 0
                    ? dataToUse?.['review_scores_rating']
                    : ''}

                  {dataToUse?.['review_scores_rating'] > 0 && (
                    <span style={{ marginLeft: '10px', marginRight: '4px' }}>
                      ( {dataToUse?.['visible_review_count']}{' '}
                      {dataToUse?.['visible_review_count'] > 1 ? 'reviews' : 'review'} )
                    </span>
                  )}

                  {isMobileView &&
                    dataToUse?.['confidenceScoreRelatedData'] &&
                    `(Data score: ${Math.floor(
                      dataToUse?.['confidenceScoreRelatedData'].dataQualityScore
                    )})`}
                </Grid>
              </Grid>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
              <Typography
                color={getTextColor('textPrimary')}
                variant="subtitle2"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Revenue Potential{' '}
                <InfoButton tooltipContent="It includes Revenue Potential (The annual revenue under the assumption that the listing is available for 365 days) of this listing. Click on the view details button below in order to see other details." />
              </Typography>
            </Box>
            <Grid sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <Typography
                color="textPrimary"
                variant="h6"
                alignItems="flex-start"
                style={{
                  color: getTextColor(POINTER_GREEN),
                  fontSize: isMobileView ? '22px' : '28px',
                  fontWeight: 900,
                }}
              >
                {revenuePotential && !isDisabled ? (
                  isAdrAnomaly ? (
                    <UnavailableIcon />
                  ) : (
                    `${formattedRevenuePotential}`
                  )
                ) : (
                  <>
                    <span
                      style={{
                        color: getTextColor(POINTER_GREEN),
                        fontSize: isMobileView ? '22px' : '28px',
                        fontWeight: 900,
                      }}
                    >
                      {currencySymbol}
                    </span>
                    <span style={styles.blurText}>11,111</span>
                  </>
                )}{' '}
                <span
                  style={{
                    color: getTextColor('#555'),
                    fontSize: isMobileView ? '16px' : '20px',
                    fontWeight: 700,
                  }}
                >
                  /year
                </span>
                <br />
              </Typography>

              {(!isDisabled || isHidden) && (
                <Grid item xs={2}>
                  {(canDelete === true || canDelete === undefined) && (
                    <InfoButton tooltipContent="By removing this listing, it won't be included in the calculation for the profit simulation, and rest of the charts." />
                  )}
                </Grid>
              )}
            </Grid>

            {isDisabled && !estimatorSampleListingData && !revenuePotential && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pt: 1,
                  width: '100%',
                }}
              >
                <Grid justifyContent="space-between" direction="row" sx={{ width: '100%' }}>
                  <Typography
                    color="textPrimary"
                    variant="subtitle2"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    ADR (Average Daily Rate) <br />
                  </Typography>
                  <Typography
                    color="textPrimary"
                    variant="h6"
                    alignItems="flex-start"
                    style={{
                      color: getTextColor(POINTER_GREEN),
                      fontSize: isMobileView ? '18px' : '22px',
                      fontWeight: 900,
                    }}
                  >
                    {isAdrAnomaly ? <UnavailableIcon /> : formattedADR}
                    <br />
                  </Typography>
                </Grid>
              </Box>
            )}
          </CardContent>
          <Grid sx={{ width: '100%' }}>
            <Button
              variant="contained"
              sx={{
                position: 'absolute',
                bottom: -20,
                right: 25,
                background: getTextColor(INDIVIDUAL_LISTING_TOOLTIP_BUTTON_COLOR),
                '&:hover': {
                  background: getTextColor(INDIVIDUAL_LISTING_TOOLTIP_BUTTON_HOVER_COLOR),
                  cursor: 'pointer',
                },
              }}
              onClick={() =>
                isBookingComListing
                  ? openListingPage(isBookingComListing)
                  : !isHidden && setOpenFullView(true)
              }
            >
              View Details
            </Button>
          </Grid>
        </Box>
      </Box>
    </Card>
  );

  const LTRPointer = () => (
    <Card sx={{ ...styles.cardStyle, maxWidth: 500 }}>
      <Box sx={{ ...styles.cardBackground, maxWidth: 500 }}>
        {isMobileView && (
          <img
            style={styles.mobileViewImage}
            alt={dataToUse?.['street_address']}
            src={getThumbnailImageUrl(dataToUse?.['thumbnail_url'])}
            onClick={() => openListingPage(true)}
          />
        )}

        {!isMobileView && (
          <CardMedia
            component="img"
            sx={{
              width: '30%',
              borderRadius: 2,
              cursor: 'pointer',
              filter: isHidden ? 'saturate(0)' : 'saturate(1)',
              opacity: isHidden ? 0.6 : 1,
            }}
            alt={dataToUse?.['street_address']}
            image={getThumbnailImageUrl(dataToUse?.['thumbnail_url'])}
            title={dataToUse?.['street_address']}
            onClick={() => openListingPage(true)}
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '500px !important' }}>
          <CardContent sx={{ flex: '1 0 auto', paddingTop: '25px' }}>
            <Typography
              component="div"
              variant="h5"
              sx={{
                fontSize: isMobileView ? '16px' : '18px',
                fontWeight: 'bold',
                pb: 1,
                color: getTextColor('#000'),
              }}
            >
              <Link
                target="_blank"
                rel="nofollow"
                color="inherit"
                underline="none"
                onClick={() => {
                  openListingPage(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                {dataToUse?.['room_type'] && `${dataToUse?.['room_type']} in `}
                {dataToUse?.['street_address']}
              </Link>
            </Typography>
            <Typography
              component={'span'}
              color={getTextColor('textSecondary')}
              sx={{ fontSize: isMobileView ? '12px' : '14px' }}
            >
              <Grid container>
                <Grid item xs={2}>
                  <HotelSharpIcon
                    style={{ ...styles.contentIconStyle, color: getTextColor(POINTER_GREEN) }}
                  />{' '}
                  {dataToUse?.['bedrooms']}
                </Grid>

                {dataToUse?.['property_size'] && (
                  <Grid item xs={10}>
                    <AspectRatioIcon
                      style={{ ...styles.contentIconStyle, color: getTextColor(POINTER_GREEN) }}
                    />{' '}
                    {formatCurrency(String(dataToUse?.['property_size']))}{' '}
                    {dataToUse?.['property_size_unit']}
                  </Grid>
                )}
              </Grid>
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                pt: 1,
                width: '100%',
              }}
            >
              <Grid justifyContent="space-between" direction="row" sx={{ width: '100%' }}>
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  Monthly Rent <br />
                </Typography>
                <Typography
                  color="textPrimary"
                  variant="h6"
                  alignItems="flex-start"
                  style={{
                    color: getTextColor(POINTER_GREEN),
                    fontSize: isMobileView ? '18px' : '22px',
                    fontWeight: 900,
                  }}
                >
                  {`${currencySymbol}${abbreviateNumber(
                    convertCurrency(currency, data?.['rent_price'])
                  )}/month`}
                  <br />
                </Typography>
              </Grid>
            </Box>
          </CardContent>
          <Grid sx={{ width: '100%' }}>
            <Button
              variant="contained"
              sx={{
                position: 'absolute',
                bottom: -20,
                right: 25,
                background: getTextColor(INDIVIDUAL_LISTING_TOOLTIP_BUTTON_COLOR),
                '&:hover': {
                  background: getTextColor(INDIVIDUAL_LISTING_TOOLTIP_BUTTON_HOVER_COLOR),
                  cursor: 'pointer',
                },
              }}
              onClick={() => openListingPage(true)}
            >
              View Details
            </Button>
          </Grid>
        </Box>
      </Box>
    </Card>
  );

  return ota === 'ltr' ? (
    <LTRPointer />
  ) : isEstimator ? (
    <EstimatorPointer isBookingComListing={isBookingComListing || false} />
  ) : matches && !showDesktopUi ? (
    <OverviewMobilePointer />
  ) : (
    <OverviewDesktopPointer />
  );
};

export default React.memo(SimplifiedAirbnbPointerBody);
